import { useEffect, useRef } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { idDoEvento } from './realtimeNegociacoes';

interface Opcoes {
    /** Só assina com usuário e funil resolvidos. Vira o nome do canal. */
    usuarioId?: string | null;
    /** `true` segura o lote sem descartá-lo: carga cheia em voo (a resposta dela viria
     *  por cima do patch) ou arraste em andamento (mexer na lista no meio do drag faz o
     *  card pular da mão do usuário). Ao voltar para `false`, o lote é aplicado. */
    pausado: boolean;
    /** Recarrega só estes ids, com o mesmo recorte da carga cheia. */
    aoAplicar: (ids: number[]) => Promise<void> | void;
    /** Saída de emergência: lote grande demais, payload truncado ou reconexão. */
    aoRecarregarTudo: () => void;
    /** Descarte barato antes de gastar uma requisição. Ver o comentário do hook. */
    deveIgnorar?: (payload: { new?: any; old?: any }) => boolean;
    /** Agrupa a rajada de eventos de uma edição em massa numa requisição só. */
    janelaMs?: number;
    /** Acima disso, uma carga cheia sai mais barata que N linhas por id. */
    limiteLote?: number;
}

/**
 * Assina `public.negociacoes` e avisa o board quais linhas mudaram.
 *
 * Decisões que não são óbvias:
 *
 * - **Sem `filter` no postgres_changes.** Filtrar por `pipeline_id=eq.X` pareceria o
 *   corte natural, mas o filtro é avaliado contra a linha *nova*: uma negociação movida
 *   para outro funil deixaria de casar e o evento não chegaria — o card ficaria na tela
 *   para sempre. Sem filtro, o board recebe tudo e decide na query de recorte.
 *
 * - **O payload é ignorado; só o id é usado.** A linha do Realtime é a linha crua da
 *   tabela: sem os embeds que o card lê (tarefas, unidades_consumidoras,
 *   analise_cadastral) e sem passar pelo recorte de papel/filtros. Aplicá-la direto
 *   pintaria card sem tarefa e furaria o escopo do vendedor. Recarregar a linha pelo
 *   PostgREST custa uma requisição minúscula e devolve exatamente o mesmo formato da
 *   carga cheia — inclusive com o RLS aplicado de novo.
 *
 * - **Não dá para pré-filtrar por funil de forma geral.** Com REPLICA IDENTITY DEFAULT
 *   (a nossa), o `old` de um UPDATE traz só a PK — não dá para saber de qual funil a
 *   linha *saiu*. Por isso o `deveIgnorar` do board só descarta evento de linha que **não
 *   está na tela** e cujo `pipeline_id` novo é outro funil (não-nulo): esse caso só poderia
 *   virar uma inclusão, e a inclusão se decide pelos valores novos. Linha que já está na
 *   tela nunca é descartada — é justamente ela que pode precisar sumir.
 *
 * - **Reconexão recarrega tudo.** Enquanto o socket esteve caído nenhum evento foi
 *   enfileirado: o que mudou nesse intervalo está perdido. Um `SUBSCRIBED` que não é o
 *   primeiro significa exatamente isso, e a única saída correta é a carga cheia.
 */
export const useNegociacoesRealtime = ({
    usuarioId,
    pausado,
    aoAplicar,
    aoRecarregarTudo,
    deveIgnorar,
    janelaMs = 400,
    limiteLote = 30,
}: Opcoes) => {
    // Callbacks por ref: mudam a cada render do board e não podem derrubar o canal.
    const aoAplicarRef = useRef(aoAplicar);
    aoAplicarRef.current = aoAplicar;
    const aoRecarregarTudoRef = useRef(aoRecarregarTudo);
    aoRecarregarTudoRef.current = aoRecarregarTudo;
    const deveIgnorarRef = useRef(deveIgnorar);
    deveIgnorarRef.current = deveIgnorar;

    const buffer = useRef<Set<number>>(new Set());
    const forcarCargaCheia = useRef(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pausadoRef = useRef(pausado);
    pausadoRef.current = pausado;

    const descarregar = useRef(() => {});
    descarregar.current = () => {
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
        // Segura o lote: o effect de `pausado` chama de novo quando destravar.
        if (pausadoRef.current) return;
        const ids = Array.from(buffer.current);
        if (!ids.length && !forcarCargaCheia.current) return;
        buffer.current.clear();

        if (forcarCargaCheia.current || ids.length > limiteLote) {
            forcarCargaCheia.current = false;
            aoRecarregarTudoRef.current();
            return;
        }
        Promise.resolve(aoAplicarRef.current(ids)).catch(err =>
            console.error('Realtime negociacoes — falha ao aplicar lote:', err));
    };

    const agendar = useRef(() => {});
    agendar.current = () => {
        if (timer.current) return;
        timer.current = setTimeout(() => descarregar.current(), janelaMs);
    };

    useEffect(() => {
        if (!usuarioId) return;
        let jaConectou = false;

        const canal = supabase
            .channel(`kanban-negociacoes-${usuarioId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'negociacoes' }, (payload) => {
                if (deveIgnorarRef.current?.(payload as any)) return;
                const id = idDoEvento(payload as any);
                if (id === null) forcarCargaCheia.current = true; // payload truncado
                else buffer.current.add(id);
                agendar.current();
            })
            .subscribe((status) => {
                if (status !== 'SUBSCRIBED') return;
                if (jaConectou) {
                    // Reassinatura depois de uma queda: houve buraco, ressincroniza.
                    forcarCargaCheia.current = true;
                    agendar.current();
                }
                jaConectou = true;
            });

        return () => {
            if (timer.current) { clearTimeout(timer.current); timer.current = null; }
            supabase.removeChannel(canal);
        };
        // `usuarioId` (string) e não o objeto `user`: o AuthContext troca a referência de
        // `user` a cada refresh de token, e depender do objeto derrubava e recriava o canal
        // — cada recriação vira uma carga cheia pelo caminho da reconexão acima.
    }, [usuarioId]);

    // Destravou (carga cheia terminou / arraste soltou): aplica o que ficou represado.
    useEffect(() => {
        if (!pausado) descarregar.current();
    }, [pausado]);
};
